/**
 * ProfilePage — Agent Profile (Screen 5). Redesigned into a 3-column dashboard layout.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import styles from './ProfilePage.module.css';

export function ProfilePage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();

  const skills = [
    { label: 'Claims Knowledge', pct: 80 },
    { label: 'Healthcare Policies', pct: 72 },
    { label: 'Compliance', pct: 95 },
    { label: 'Customer Communication', pct: 88 },
    { label: 'Product Knowledge', pct: 78 }
  ];

  const certifications = useMemo(
    () => [
      { id: '1', title: 'Claims Handling Essentials', issuedDate: '10 Feb 2026', expiryDate: '09 Feb 2027', status: 'Active' },
      { id: '2', title: 'Insurance Fundamentals', issuedDate: '15 Jan 2026', expiryDate: '14 Jan 2027', status: 'Active' },
      { id: '3', title: 'HIPAA Compliance 2026', issuedDate: '05 Mar 2026', expiryDate: '04 Mar 2027', status: 'Active' },
      { id: '4', title: 'Fraud Detection Specialist', issuedDate: '20 Apr 2026', expiryDate: '19 Apr 2027', status: 'Active' },
    ],
    []
  );

  const initials = (user?.full_name ?? 'Dimple Sharma')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const badges = [
    { name: 'Compliance Star', date: 'Earned on 15 May 2026', color: '#4caf50' },
    { name: 'Claims Expert', date: 'Earned on 20 Apr 2026', color: '#2196f3' },
    { name: 'Dedicated Learner', date: 'Earned on 10 Mar 2026', color: '#ff9800' },
    { name: 'Risk Assessment Pro', date: 'Earned on 05 Jan 2026', color: '#9c27b0' },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        
        {/* ── Column 1: Profile Summary & Streak ── */}
        <div className={styles.column1}>
          <div className={styles.profileCard}>
            <div className={styles.avatarWrapper}>
              <div className={styles.avatar}>{initials}</div>
            </div>
            <h2 className={styles.profileName}>{user?.full_name ?? 'Dimple Sharma'}</h2>
            <p className={styles.profileRole}>Senior Claims Agent</p>
            <p className={styles.profileMetaSub}>Emp ID: AGT-10045</p>
            <p className={styles.profileMetaSub}>Claims Department</p>

            <div className={styles.metaDivider} />

            <dl className={styles.metaList}>
              <div className={styles.metaRow}>
                <dt className={styles.metaKey}>Role</dt>
                <dd className={styles.metaVal}>{user?.role ?? 'Claims Agent'}</dd>
              </div>
              <div className={styles.metaRow}>
                <dt className={styles.metaKey}>Region</dt>
                <dd className={styles.metaVal}>{user?.country ?? 'APAC'}</dd>
              </div>
              <div className={styles.metaRow}>
                <dt className={styles.metaKey}>Language</dt>
                <dd className={styles.metaVal}>{user?.language ?? 'English'}</dd>
              </div>
              <div className={styles.metaRow}>
                <dt className={styles.metaKey}>Manager</dt>
                <dd className={styles.metaVal}>John Smith</dd>
              </div>
              <div className={styles.metaRow}>
                <dt className={styles.metaKey}>Joined On</dt>
                <dd className={styles.metaVal}>12 Jan 2023</dd>
              </div>
            </dl>
          </div>

          {/* Learning Streak */}
          <div className={styles.streakCard}>
            <h3 className={styles.streakTitle}>🔥 Learning Streak</h3>
            <div className={styles.streakRow}>
              <span className={styles.streakNumber}>5</span>
              <span className={styles.streakUnit}>Days</span>
            </div>
            <p className={styles.streakSubtitle}>Keep it up!</p>
            <div className={styles.streakDots}>
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                <div key={day} className={styles.dayDotContainer}>
                  <div className={`${styles.dayDot} ${i < 5 ? styles.dayDotActive : ''}`} />
                  <span className={styles.dayLabel}>{day}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Column 2: Skills Matrix & Certifications ── */}
        <div className={styles.column2}>
          {/* Skills Matrix */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Skills Matrix</h3>
            <div className={styles.skillsList}>
              {skills.map(({ label, pct }) => (
                <div key={label} className={styles.skillRow}>
                  <span className={styles.skillLabel}>{label}</span>
                  <div className={styles.skillBarWrapper}>
                    <div className={styles.skillBar} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={styles.skillPct}>{pct}%</span>
                </div>
              ))}
            </div>
          </section>

          {/* Certifications Table */}
          <section className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <h3 className={styles.sectionTitle}>Certifications</h3>
              <button className={styles.viewAllLink} onClick={() => navigate('/history')}>
                View All
              </button>
            </div>

            <div className={styles.tableWrapper}>
              <table className={styles.certTable}>
                <thead>
                  <tr>
                    <th className={styles.th}>Certificate</th>
                    <th className={styles.th}>Issue Date</th>
                    <th className={styles.th}>Expiry Date</th>
                    <th className={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {certifications.map((cert) => (
                    <tr key={cert.id} className={styles.tr}>
                      <td className={styles.td}>
                        <span className={styles.certTitle}>{cert.title}</span>
                      </td>
                      <td className={styles.td}>{cert.issuedDate}</td>
                      <td className={styles.td}>{cert.expiryDate}</td>
                      <td className={styles.td}>
                        <span className={styles.statusBadgeActive}>{cert.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* ── Column 3: Performance Summary & Badges ── */}
        <div className={styles.column3}>
          {/* Performance Summary */}
          <div className={styles.performanceSection}>
            <h3 className={styles.sectionTitle}>Performance Summary</h3>
            <div className={styles.performanceGrid}>
              <div className={styles.perfCard}>
                <span className={styles.perfIcon}>📘</span>
                <div>
                  <div className={styles.perfVal}>5</div>
                  <div className={styles.perfLabel}>Courses Completed</div>
                </div>
              </div>
              <div className={styles.perfCard}>
                <span className={styles.perfIcon}>⏱</span>
                <div>
                  <div className={styles.perfVal}>24.5</div>
                  <div className={styles.perfLabel}>Learning Hours</div>
                </div>
              </div>
              <div className={styles.perfCard}>
                <span className={styles.perfIcon}>🏆</span>
                <div>
                  <div className={styles.perfVal}>8</div>
                  <div className={styles.perfLabel}>Assessments Passed</div>
                </div>
              </div>
              <div className={styles.perfCard}>
                <span className={styles.perfIcon}>🎯</span>
                <div>
                  <div className={styles.perfVal}>84%</div>
                  <div className={styles.perfLabel}>Average Score</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Badges */}
          <section className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <h3 className={styles.sectionTitle}>Recent Badges</h3>
              <button className={styles.viewAllLink} onClick={() => navigate('/history')}>
                View All
              </button>
            </div>
            <ul className={styles.badgeList}>
              {badges.map((badge) => (
                <li key={badge.name} className={styles.badgeItem}>
                  <div className={styles.badgeIcon} style={{ background: badge.color }}>
                    🥇
                  </div>
                  <div className={styles.badgeInfo}>
                    <div className={styles.badgeName}>{badge.name}</div>
                    <div className={styles.badgeDate}>{badge.date}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

      </div>
    </div>
  );
}
